import { isBefore, isSameDay } from "date-fns";
import { createSelector } from "reselect";

import { TEmitent } from "@app/types/insider";

import { schema } from "../schema";

export const normalizeCIF = (cif: string) => {
  cif = (cif || "").trim();
  cif = cif.replace(/RO/gi, "");
  return cif;
};

export const selectfiscalBookInvoice = createSelector(
  schema.fiscalBooks.selectTableAsList, //should be named fiscalBooks
  schema.invoiceIssuer.selectTableAsList,
  schema.invoiceFiscal.selectTableAsList,
  schema.receiptFiscal.selectTableAsList,
  (fiscalBookInvoice, issuer, invoices, receipts) => {
    return fiscalBookInvoice.map((serial) => {
      const emitent = issuer.find(
        (i: TEmitent) =>
          normalizeCIF(i.CIF) === normalizeCIF(serial.CIF_EMITENT)
      );
      const type = serial.CHITANTA === 1 ? "Chitantier" : "Facturier";
      const activated = serial?.ACTIV_DELA
        ? new Date(serial.ACTIV_DELA).toLocaleDateString()
        : null;
      const inactivated = serial.INACTIV_DELA
        ? new Date(serial.INACTIV_DELA).toLocaleDateString()
        : null;
      const note00 = inactivated
        ? `Inactivat de la: ${inactivated}`
        : activated
        ? `Activ de la: ${activated}`
        : `Nu este activ`;

      return {
        ...serial,
        DENUMIRE_EMITENT: emitent?.DENUMIRE || "",
        text: `${type}: ${serial.PREFIX}${serial.NUMAR}${serial.SUFIX}   -   ${note00}`,
        ELEMENT_COUNT: 0,
      };
    });
  }
);

const invoiceCountByBook = createSelector(
  schema.invoiceFiscal.selectTableAsList,
  (invoices) => {
    return invoices.reduce((acc, inv) => {
      const book = inv.FACTURIER_ID;
      if (!acc[book]) {
        acc[book] = 0;
      }
      acc[book]++;
      return acc;
    }, {} as Record<string, number>);
  }
);

const receiptCountByBook = createSelector(
  schema.receiptFiscal.selectTableAsList,
  (receipts) => {
    return receipts.reduce((acc, inv) => {
      const book = inv.FACTURIER_ID;
      if (!acc[book]) {
        acc[book] = 0;
      }
      acc[book]++;
      return acc;
    }, {} as Record<string, number>);
  }
);

export const selectfiscalBooks = createSelector(
  schema.fiscalBooks.selectTableAsList, //should be named fiscalBooks
  schema.invoiceIssuer.selectTableAsList,
  invoiceCountByBook,
  receiptCountByBook,
  (fiscalBooks, issuer, iBooksIdx, rBooksIdx) => {
    return fiscalBooks.map((serial) => {
      const emitent = issuer.find(
        (i: TEmitent) =>
          normalizeCIF(i.CIF) === normalizeCIF(serial.CIF_EMITENT)
      );
      const type = serial.CHITANTA === 1 ? "Chitantier" : "Facturier";
      const activated = serial?.ACTIV_DELA
        ? new Date(serial.ACTIV_DELA).toLocaleDateString()
        : null;
      const inactivated = serial.INACTIV_DELA
        ? new Date(serial.INACTIV_DELA).toLocaleDateString()
        : null;
      const note00 = inactivated
        ? `Inactivat de la: ${inactivated}`
        : activated
        ? `Activ de la: ${activated}`
        : `Nu este activ`;

      const isActive =
        (isBefore(new Date(serial.ACTIV_DELA), new Date()) ||
          isSameDay(new Date(serial.ACTIV_DELA), new Date())) &&
        (!serial.INACTIV_DELA ||
          isBefore(new Date(), new Date(serial.INACTIV_DELA)));

      return {
        ...serial,
        DENUMIRE_EMITENT: emitent?.DENUMIRE,
        text: `${type}: ${serial.PREFIX}${serial.NUMAR}${serial.SUFIX}   -   ${note00}`,
        ELEMENT_COUNT:
          +serial.CHITANTA === 1
            ? rBooksIdx[serial.FCT_FF] || 0
            : iBooksIdx[serial.FCT_FF] || 0,
        isActive,
      };
    });
  }
);

const extractNumbersFromSeries = (series) => {
  return series.reduce((acc, item) => {
    acc[item.parsedNumber] = (acc[item.parsedNumber] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);
};

export const selectIssuedSeriesOnCurrentFiscalBook = createSelector(
  schema.fiscalBooks.selectTableAsList,
  schema.uiFiscalBooks.select,
  schema.invoiceFiscal.selectTableAsList,
  schema.receiptFiscal.selectTableAsList,
  (books, selectedBookIdContainer, invoices, receipts) => {
    const selectedBookId = selectedBookIdContainer.selectedBookId;
    const book = books.find((b) => b.FCT_FF === selectedBookId);
    if (!book) return [];

    const filteredDocs = book.CHITANTA === 1 ? receipts : invoices;
    const docTypePrefix = book.CHITANTA === 1 ? "R" : "I";
    const fields =
      book.CHITANTA === 1
        ? {
            idField: "ID_CHITANTA",
            numberField: "NR_F_FISC",
            dateField: "DATA",
          }
        : { idField: "FF_ID", numberField: "NR_FF", dateField: "LA_DATA" };

    const extractNumber = (serial) =>
      parseInt(
        (serial || "").replace(book.PREFIX, "").replace(book.SUFIX, ""),
        10
      );

    return filteredDocs
      .filter((doc) => doc.FACTURIER_ID === selectedBookId)
      .map((doc) => ({
        id: `${docTypePrefix}${doc[fields.idField]}`,
        text: doc[fields.numberField],
        date: doc[fields.dateField],
        parsedNumber: extractNumber(doc[fields.numberField]),
      }))
      .sort((a, b) => a.parsedNumber - b.parsedNumber);
  }
);

// This selector finds non-unique (duplicate) numbers.
export const selectScanForNotUniqueNumbers = createSelector(
  selectIssuedSeriesOnCurrentFiscalBook,
  (series) => {
    const numberCounts: Record<number, number> =
      extractNumbersFromSeries(series);
    return Object.entries(numberCounts).filter(([_, count]) => count > 1);
  }
);

// This selector checks for gaps in the sequence.
export const selectScanForSeriesNotInPerfectSequence = createSelector(
  selectIssuedSeriesOnCurrentFiscalBook,
  (series) => {
    const sortedNumbers = series
      .map((item) => item.parsedNumber)
      .sort((a, b) => a - b);
    const missingNumbers = [];
    for (let i = 0; i < sortedNumbers.length - 1; i++) {
      if (sortedNumbers[i + 1] - sortedNumbers[i] > 1) {
        // This loop fills the range of missing numbers
        for (let j = sortedNumbers[i] + 1; j < sortedNumbers[i + 1]; j++) {
          missingNumbers.push(j);
        }
      }
    }
    return missingNumbers;
  }
);

export const aggregateSeries = createSelector(
  selectIssuedSeriesOnCurrentFiscalBook,
  selectScanForNotUniqueNumbers,
  selectScanForSeriesNotInPerfectSequence,
  (series, duplicates, gaps) => {
    // Convert duplicates and gaps for faster checks
    const duplicateNumbers = new Set(
      duplicates.map(([number]) => parseInt(number))
    );
    const gapNumbers = new Set(gaps);

    return series.map((item, index, arr) => {
      const isDuplicate = duplicateNumbers.has(item.parsedNumber);
      let isStartingGap = false;
      let isEndingGap = false;

      // Check if this item starts a gap
      if (!gapNumbers.has(item.parsedNumber) && index < arr.length - 1) {
        // If the next item's number isn't the current one + 1, this starts a gap
        isStartingGap = arr[index + 1].parsedNumber !== item.parsedNumber + 1;
      }

      // Check if this item ends a gap
      if (!gapNumbers.has(item.parsedNumber) && index > 0) {
        // If the previous item's number isn't the current one - 1, this ends a gap
        isEndingGap = arr[index - 1].parsedNumber !== item.parsedNumber - 1;
      }

      return {
        ...item, // Spread the existing properties
        status: isDuplicate
          ? "Duplicate"
          : isStartingGap || isEndingGap
          ? "Gap"
          : "OK",
        isDuplicate,
        isStartingGap,
        isEndingGap,
      };
    });
  }
);
